// Importado pelo importmap, não por caminho relativo. O caminho relativo
// resolvia para uma URL sem o ?v= do Shopify, então o navegador guardava o
// módulo para sempre: depois de qualquer atualização o import quebrava com
// "does not provide an export named" e derrubava o módulo inteiro, deixando a
// página sem galeria, sem quantidade e sem carrinho. O apelido está registrado
// em snippets/scripts.liquid e carrega a URL versionada.
import { adicionarAoCarrinho } from '@ninetails/carrinho';

// Página de produto: galeria com miniaturas, quantidade e envio ao carrinho.
//
// O envio é por fetch para /cart/add.js, para a cliente não perder a página.
// Falhas de rede oferecem consulta ao carrinho sem repetir o POST.

function setup(root) {
  if (root.dataset.ntProdutoPronto === 'true') return;

  // ---------- galeria ----------
  const fotos = Array.from(root.querySelectorAll('[data-nt-foto]'));
  const minis = Array.from(root.querySelectorAll('[data-nt-mini]'));

  let fotoAtual = 0;
  const mostrar = (i, metodo) => {
    const mudou = fotoAtual !== i;
    fotoAtual = i;
    fotos.forEach((f, n) => {
      f.classList.toggle('is-ativa', n === i);
      f.setAttribute('aria-hidden', String(n !== i));
    });
    minis.forEach((m, n) => {
      m.classList.toggle('is-ativa', n === i);
      m.setAttribute('aria-selected', String(n === i));
      m.tabIndex = n === i ? 0 : -1;
    });
    if (mudou && metodo) document.dispatchEvent(new CustomEvent('nt:interaction', {
      detail: { name: 'nt_gallery_view', parameters: { image_index: i + 1, interaction_method: metodo } },
    }));
  };

  minis.forEach((m, i) => {
    m.addEventListener('click', () => mostrar(i, 'thumbnail'));
    m.addEventListener('keydown', (event) => {
      let next = i;
      if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (i + 1) % minis.length;
      else if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = (i - 1 + minis.length) % minis.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = minis.length - 1;
      else return;
      event.preventDefault();
      mostrar(next, 'keyboard');
      minis[next].focus();
    });
  });
  if (fotos.length) mostrar(0);

  const palco = root.querySelector('.nt-pdp__palco');
  let toque = null;
  if (palco && fotos.length > 1) {
    palco.addEventListener('touchstart', (event) => {
      toque = event.touches.length === 1
        ? { x: event.touches[0].clientX, y: event.touches[0].clientY, time: performance.now() } : null;
    }, { passive: true });
    palco.addEventListener('touchmove', (event) => {
      if (event.touches.length !== 1) toque = null;
    }, { passive: true });
    palco.addEventListener('touchcancel', () => { toque = null; }, { passive: true });
    palco.addEventListener('touchend', (event) => {
      const inicio = toque;
      toque = null;
      if (!inicio || event.touches.length || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - inicio.x;
      const dy = event.changedTouches[0].clientY - inicio.y;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.4 || performance.now() - inicio.time > 900) return;
      mostrar((fotoAtual + (dx < 0 ? 1 : -1) + fotos.length) % fotos.length, 'swipe');
    }, { passive: true });
  }

  // ---------- quantidade ----------
  const campo = root.querySelector('[data-nt-qtd]');
  const passo = (delta) => {
    if (!campo) return;
    campo.value = Math.max(1, (parseInt(campo.value, 10) || 1) + delta);
  };
  root.querySelector('[data-nt-menos]')?.addEventListener('click', () => passo(-1));
  root.querySelector('[data-nt-mais]')?.addEventListener('click', () => passo(1));

  // ---------- carrinho ----------
  const form = root.querySelector('[data-nt-form]');
  const destino = root.querySelector('[data-nt-destino]');
  const aviso = root.querySelector('[data-nt-aviso]');
  const botao = root.querySelector('[data-nt-adicionar]');
  const sticky = root.querySelector('[data-nt-sticky]');
  const stickyAdd = root.querySelector('[data-nt-sticky-add]');
  const verCarrinho = root.querySelector('[data-nt-ver-carrinho]');
  let enviando = false;

  if (sticky && botao && 'IntersectionObserver' in window) {
    let produtoVisivel = true;
    let botaoVisivel = true;
    const atualizarSticky = () => { sticky.hidden = botaoVisivel || !produtoVisivel; };
    const observer = new IntersectionObserver(([entry]) => {
      botaoVisivel = entry.isIntersecting;
      atualizarSticky();
    });
    const rootObserver = new IntersectionObserver(([entry]) => {
      produtoVisivel = entry.isIntersecting;
      atualizarSticky();
    });
    observer.observe(botao);
    rootObserver.observe(root);
  }

  const dizer = (texto, erro) => {
    if (!aviso) return;
    aviso.textContent = texto;
    aviso.classList.toggle('is-erro', !!erro);
    aviso.hidden = !texto;
  };

  form?.addEventListener('submit', async (evento) => {
    // Compra imediata precisa do envio tradicional: quem manda é o return_to.
    if (destino && ['/checkout', '/cart'].includes(destino.value)) return;
    if (!window.fetch) return;

    evento.preventDefault();
    if (enviando || !form.reportValidity()) return;
    enviando = true;
    // Só o span do rótulo. Mexer no textContent do botão inteiro apagava os
    // dois filhos (rótulo e preço) e devolvia tudo como texto cru, então o
    // botão perdia o preço formatado para sempre depois da primeira adição.
    const rotulo = botao?.querySelector('.nt-pdp__carrinho-texto') || botao;
    const rotuloOriginal = rotulo?.textContent;
    if (botao) botao.disabled = true;
    if (stickyAdd) stickyAdd.disabled = true;
    if (verCarrinho) verCarrinho.hidden = true;
    if (rotulo) rotulo.textContent = botao.dataset.ocupado || 'Adicionando…';
    dizer('');

    try {
      await adicionarAoCarrinho(form, botao || form);
      dizer(root.dataset.avisoOk || 'Adicionado ao carrinho.');
      if (verCarrinho) verCarrinho.hidden = false;
    } catch (erro) {
      dizer(erro.message || 'Não foi possível adicionar. Tente de novo.', true);
      if (verCarrinho) verCarrinho.hidden = false;
    } finally {
      enviando = false;
      if (botao) botao.disabled = false;
      if (stickyAdd) stickyAdd.disabled = false;
      if (rotulo && rotuloOriginal) rotulo.textContent = rotuloOriginal;
    }
  });

  root.querySelector('[data-nt-agora]')?.addEventListener('click', () => {
    if (!form || !destino) return;
    destino.value = '/cart';
    form.requestSubmit ? form.requestSubmit() : form.submit();
    destino.value = 'back';
  });

  // ---------- sanfona dos detalhes ----------
  const itens = Array.from(root.querySelectorAll('[data-nt-detalhe]'));
  itens.forEach((item) => {
    item.querySelector('[data-nt-detalhe-botao]')?.addEventListener('click', () => {
      const aberto = item.classList.contains('is-aberto');
      itens.forEach((o) => {
        o.classList.remove('is-aberto');
        o.querySelector('[data-nt-detalhe-botao]')?.setAttribute('aria-expanded', 'false');
        o.querySelector('.nt-pdp__painel')?.setAttribute('inert', '');
      });
      if (!aberto) {
        item.classList.add('is-aberto');
        item.querySelector('[data-nt-detalhe-botao]')?.setAttribute('aria-expanded', 'true');
        item.querySelector('.nt-pdp__painel')?.removeAttribute('inert');
      }
    });
  });

  root.dataset.ntProdutoPronto = 'true';
}

const init = () => document.querySelectorAll('[data-nt-produto]').forEach(setup);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('[data-nt-produto]').forEach(setup);
});
